import { useState, useEffect } from 'react';
import { hashtagsAPI } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Hash, Check, X } from 'lucide-react';

/**
 * Filtre multi-sélection par hashtags # (logique "OU").
 * Réutilisé sur la liste Prospects et le Dashboard.
 * Props :
 *  - selected: string[] (ids des # sélectionnés)
 *  - onChange: (ids: string[]) => void
 */
export function HashtagFilter({ selected = [], onChange, className = '' }) {
    const [catalog, setCatalog] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        hashtagsAPI.list().then(r => setCatalog(r.data || [])).catch(() => {});
    }, []);

    if (catalog.length === 0) return null;

    const toggle = (id) => {
        const ids = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
        onChange(ids);
    };

    const count = selected.length;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" className={`justify-start ${className}`} data-testid="hashtag-filter">
                    <Hash className="w-4 h-4 mr-2 text-slate-500" />
                    {count === 0 ? 'Hashtags' : `${count} hashtag${count > 1 ? 's' : ''}`}
                    {count > 0 && (
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); onChange([]); }}
                            className="ml-2 rounded p-0.5 hover:bg-slate-100"
                            data-testid="hashtag-filter-clear"
                        >
                            <X className="w-3.5 h-3.5 text-slate-400" />
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-2" align="start">
                <p className="px-1 pb-1.5 text-xs font-medium text-slate-500">Filtrer par hashtag</p>
                <div className="max-h-56 overflow-y-auto space-y-0.5">
                    {catalog.map(h => {
                        const on = selected.includes(h.id);
                        return (
                            <button key={h.id} type="button" onClick={() => toggle(h.id)}
                                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-slate-50"
                                data-testid={`hashtag-filter-option-${h.id}`}>
                                <Badge style={{ backgroundColor: h.color, color: '#fff' }} className="gap-0.5 border-0 text-[11px]">
                                    <Hash className="w-3 h-3" />{h.label}
                                </Badge>
                                <span className="flex-1" />
                                {on && <Check className="w-4 h-4 text-green-600" />}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}
